<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import AppMenuBarWithDateRange from '@/components/Menu/AppMenuBarWithDateRange.vue'
import { ArrowLeft, RefreshCw } from 'lucide-vue-next'
import { type LifecycleState, LIFECYCLE_STATE_OPTIONS } from '@/models/MetricsSnapshot'
import aplosApi from '@/services/aplosApi'

const props = withDefaults(defineProps<{
  tenantId: string
  tenantName: string
  userId: string
  userName: string
  status?: string
  lifecycleState?: LifecycleState
}>(), {
  lifecycleState: 'all',
})

const emit = defineEmits<{
  'navigate-to-summary': []
  'navigate-to-tenant': [payload: { tenantId: string; tenantName: string }]
  'navigate-to-user': [payload: { tenantId: string; tenantName: string; userId: string; userName: string }]
  'navigate-to-execution-detail': [payload: { executionId: string; executionIds: string[] }]
  'update:lifecycleState': [state: LifecycleState]
}>()

// --- State ---

interface DrilldownExecution {
  executionId: string
  name: string
  status: string
  lifecycleState: string
  ownerName: string
  ownerEmail: string
  workflowType: string
  startedUtc: string | null
  createdUtc: string | null
  durationHuman: string
  isRoot: boolean
}

const executions = ref<DrilldownExecution[]>([])
const nextKey = ref<string | undefined>(undefined)
const loading = ref(false)
const loadingMore = ref(false)
const error = ref<string | null>(null)
const selectedLifecycleState = ref<LifecycleState>(props.lifecycleState ?? 'all')

// Reload when lifecycle state changes
watch(selectedLifecycleState, () => {
  loadInitial()
})

function handleLifecycleStateChange(value: LifecycleState) {
  selectedLifecycleState.value = value
  emit('update:lifecycleState', value)
}

// --- Status label mapping ---

const STATUS_LABEL_MAP: Record<string, string> = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  cancelled: 'Cancelled',
  throttled: 'Throttled',
  rejected: 'Rejected',
}

const statusLabel = computed(() => {
  if (!props.status) return 'Total'
  return STATUS_LABEL_MAP[props.status] ?? props.status
})

const statusBadgeClass = computed(() => {
  switch (props.status) {
    case 'succeeded': return 'bg-green-500 hover:bg-green-500 text-white'
    case 'failed': return 'bg-red-500 hover:bg-red-500 text-white'
    case 'running': return 'bg-blue-500 hover:bg-blue-500 text-white'
    case 'pending': return 'bg-yellow-500 hover:bg-yellow-500 text-white'
    case 'cancelled': return 'bg-gray-500 hover:bg-gray-500 text-white'
    case 'throttled': return 'bg-orange-500 hover:bg-orange-500 text-white'
    case 'rejected': return 'bg-red-500 hover:bg-red-500 text-white'
    default: return 'bg-blue-500 hover:bg-blue-500 text-white'
  }
})

// --- Data fetching ---

async function fetchExecutions(startKey?: string) {
  try {
    const response = await aplosApi.getDrilldownExecutions(
      props.tenantId,
      props.userId,
      {
        status: props.status,
        limit: 25,
        startKey: startKey,
      },
    )
    return response.data
  } catch (err: any) {
    throw err
  }
}

async function loadInitial() {
  loading.value = true
  error.value = null
  executions.value = []
  nextKey.value = undefined

  try {
    const data = await fetchExecutions()
    executions.value = data.executions ?? []
    nextKey.value = data.nextKey
  } catch (err: any) {
    error.value = err?.message ?? 'Failed to load executions'
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!nextKey.value || loadingMore.value) return

  loadingMore.value = true
  try {
    const data = await fetchExecutions(nextKey.value)
    const newExecutions = data.executions ?? []
    executions.value = [...executions.value, ...newExecutions]
    nextKey.value = data.nextKey
  } catch (err: any) {
    error.value = err?.message ?? 'Failed to load more executions'
  } finally {
    loadingMore.value = false
  }
}

// --- Table columns ---

const columns = [
  {
    title: 'Name',
    key: 'name',
    dataIndex: 'name',
  },
  {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
  },
  {
    title: 'Started',
    key: 'startedUtc',
    dataIndex: 'startedUtc',
  },
  {
    title: 'Duration',
    key: 'durationHuman',
    dataIndex: 'durationHuman',
  },
]

// --- Formatting helpers ---

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatusBadgeClass(status?: string): string {
  switch (status) {
    case 'succeeded': return 'bg-green-500 hover:bg-green-500 text-white'
    case 'failed': return 'bg-red-500 hover:bg-red-500 text-white'
    case 'running': return 'bg-blue-500 hover:bg-blue-500 text-white'
    case 'pending': return 'bg-yellow-500 hover:bg-yellow-500 text-white'
    case 'cancelled': return 'bg-gray-500 hover:bg-gray-500 text-white'
    case 'throttled': return 'bg-orange-500 hover:bg-orange-500 text-white'
    case 'rejected': return 'bg-red-500 hover:bg-red-500 text-white'
    default: return 'bg-gray-500 hover:bg-gray-500 text-white'
  }
}

// --- Navigation ---

function handleNavigateToSummary() {
  emit('navigate-to-summary')
}

function handleNavigateToTenant() {
  emit('navigate-to-tenant', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
  })
}

function handleNavigateToUser() {
  emit('navigate-to-user', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
    userId: props.userId,
    userName: props.userName,
  })
}

function handleRowClick(record: DrilldownExecution) {
  if (record.executionId) {
    const ids = executions.value.map(e => e.executionId).filter(Boolean)
    emit('navigate-to-execution-detail', { executionId: record.executionId, executionIds: ids })
  }
}

// --- Menu bar buttons ---

const navButtons = ref([
  {
    icon: ArrowLeft,
    onClick: handleNavigateToUser,
    title: '',
    toolTip: 'Back to User',
  },
  {
    icon: RefreshCw,
    onClick: loadInitial,
    animate: loading,
    title: '',
    toolTip: 'Refresh',
  },
])

// --- Lifecycle ---

onMounted(() => {
  loadInitial()
})
</script>

<template>
  <div>
    <!-- Menu bar with back button -->
    <AppMenuBarWithDateRange
      :showSearch="false"
      :showDateRange="false"
      :buttons="navButtons"
    />

    <!-- Breadcrumb navigation -->
    <nav class="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToSummary"
      >
        Summary
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToTenant"
      >
        {{ tenantName }}
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToUser"
      >
        {{ userName }}
      </button>
      <span class="mx-1">&gt;</span>
      <span class="text-foreground font-medium">
        <Badge :class="statusBadgeClass">{{ statusLabel }}</Badge>
      </span>
    </nav>

    <!-- Status heading -->
    <div class="mb-4">
      <h2 class="text-lg font-semibold text-foreground flex items-center gap-2">
        Executions —
        <Badge :class="statusBadgeClass">{{ statusLabel }}</Badge>
      </h2>
    </div>

    <!-- Lifecycle state filter -->
    <div class="mb-4">
      <a-segmented
        v-model:value="selectedLifecycleState"
        :options="LIFECYCLE_STATE_OPTIONS"
        @change="handleLifecycleStateChange"
      />
    </div>

    <!-- Error state -->
    <div v-if="error && !loading" class="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-950">
      <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y-3">
      <Skeleton v-for="i in 5" :key="i" class="h-12 w-full" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!error && executions.length === 0" class="py-12 text-center text-muted-foreground">
      No executions found for this status.
    </div>

    <!-- Execution table -->
    <div v-else-if="!error" class="overflow-x-auto">
      <a-table
        :dataSource="executions"
        :columns="columns"
        :pagination="false"
        :rowKey="(record: DrilldownExecution) => record.executionId ?? ''"
        :customRow="(record: DrilldownExecution) => ({
          onClick: () => handleRowClick(record),
          style: 'cursor: pointer',
        })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            {{ record.name || record.executionId || '—' }}
          </template>

          <template v-else-if="column.key === 'status'">
            <Badge :class="getStatusBadgeClass(record.status)">
              {{ record.status ?? '—' }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'startedUtc'">
            {{ formatDate(record.startedUtc || record.createdUtc) }}
          </template>

          <template v-else-if="column.key === 'durationHuman'">
            {{ record.durationHuman || '—' }}
          </template>
        </template>
      </a-table>

      <!-- Load More button -->
      <div v-if="nextKey" class="mt-4 flex justify-center">
        <button
          class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <span v-if="loadingMore">Loading...</span>
          <span v-else>Load More</span>
        </button>
      </div>
    </div>
  </div>
</template>
